/**
 * Document Reader Abstraction
 * 
 * Unified interface for reading ANY document format:
 * - Y.js documents (live, collaborative docs)
 * - Simple node documents (PDFs, Word, read-only files)
 * 
 * This abstraction allows doc_read_v2 to work with all formats uniformly
 * without knowing the underlying storage mechanism.
 * 
 * Architecture:
 * - DocumentReader: Common interface for all document types
 * - YjsDocumentReader: Wraps existing Y.js operations
 * - SimpleNodeDocumentReader: Direct reading of simple node JSON
 * - createDocumentReader: Factory that selects appropriate reader
 */

import { ReadResult } from './internal/yDocHighLevelAPI';
import { CommandExecutionContext } from '../../../../types/commands';
import { entityService } from '../../../../services/entityService';
import { getDocContext as getYjsDocContext } from './docApi';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('DOCUMENT_READER_FACTORY');

// Forward declarations for readers (lazy loaded to avoid circular dependencies)
let SimpleNodeDocumentReaderClass: any = null;
let YjsDocumentReaderClass: any = null;

/**
 * Document Reader Interface
 * 
 * All document formats (Y.js, PDF, Word, etc.) implement this interface,
 * enabling uniform access in doc_read_v2 command.
 */
export interface DocumentReader {
  /**
   * Document metadata
   */
  readonly documentId: string;
  readonly documentName: string;
  readonly format: 'yjs' | 'simple_nodes';
  
  /**
   * Read all nodes in the document
   * 
   * Returns all nodes with standard structure:
   * { id, type, text, attrs }
   */
  readAll(): ReadResult;
  
  /**
   * Read nodes by index range (0-based, inclusive)
   * 
   * @param fromIndex - Start index
   * @param toIndex - End index (inclusive)
   * 
   * Example: readIndexRange(0, 4) reads first 5 nodes
   */
  readIndexRange(fromIndex: number, toIndex: number): ReadResult;
  
  /**
   * Read nodes by node ID range
   * 
   * @param fromId - Start node ID
   * @param toId - End node ID
   * 
   * Returns all nodes between fromId and toId (inclusive)
   */
  readNodeRange(fromId: string, toId: string): ReadResult;
  
  /**
   * Search for nodes containing query text
   * 
   * @param query - Search query (case-insensitive)
   * @param options - Search options (fuzzy matching, threshold, limit)
   * 
   * Returns nodes where text content contains query
   */
  search(query: string, options?: { fuzzy?: boolean; fuzzyThreshold?: number; limit?: number }): ReadResult;
  
  /**
   * Get document statistics
   * 
   * Returns basic document metrics for context
   */
  getStats(): {
    totalNodes: number;
    totalCharacters: number;
    totalWords: number;
  };
}

/**
 * Factory: Create appropriate document reader based on storage format
 * 
 * Decision tree:
 * 1. Fetch entity from database
 * 2. Check for skeleton.pdfAsDocument or skeleton.wordAsDocument
 *    → Use SimpleNodeDocumentReader (direct reading, no Y.js)
 * 3. Otherwise
 *    → Use YjsDocumentReader (existing Y.js document path)
 * 
 * This factory encapsulates all format detection logic,
 * keeping doc_read_v2 command clean and format-agnostic.
 * 
 * @param documentId - Entity ID or LLM ID
 * @param context - Command execution context
 * @returns Reader instance or error
 */
export async function createDocumentReader(
  documentId: string,
  context: CommandExecutionContext
): Promise<{ reader: DocumentReader | null; error?: string }> {
  
  try {
    log.log('[createDocumentReader] Creating reader for document:', {
      docId: documentId.substring(Math.max(0, documentId.length - 8))
    });
    
    // Fetch entity to determine storage format
    const entity = await entityService.fetchEntity(documentId);
    
    if (!entity) {
      log.error('[createDocumentReader] Entity not found:', documentId);
      return {
        reader: null,
        error: `Entity not found: ${documentId}`
      };
    }
    
    const documentName = entity.name || 'Untitled';
    
    // Check for simple node formats (PDF, Word)
    const pdfAsDocument = entity.skeleton?.pdfAsDocument;
    const wordAsDocument = entity.skeleton?.wordAsDocument;
    
    if (pdfAsDocument || wordAsDocument) {
      // Simple node format detected - use direct reader (no Y.js)
      const simpleDocData = (pdfAsDocument || wordAsDocument)!;
      const docType = pdfAsDocument ? 'PDF' : 'Word';
      
      log.log(`[createDocumentReader] ✅ Creating SimpleNodeReader for ${docType}:`, {
        docId: documentId.substring(Math.max(0, documentId.length - 8)),
        documentName,
        format: 'simple_nodes'
      });
      
      // Lazy load to avoid circular dependencies
      if (!SimpleNodeDocumentReaderClass) {
        const module = await import('./readers/SimpleNodeDocumentReader');
        SimpleNodeDocumentReaderClass = (module as any).SimpleNodeDocumentReader;
      }
      
      const reader = new SimpleNodeDocumentReaderClass(
        documentId,
        documentName,
        simpleDocData
      );
      
      log.log('[createDocumentReader] 🔍 CRITICAL: SimpleNodeReader created:', {
        documentId: documentId.substring(Math.max(0, documentId.length - 8)),
        format: reader.format,
        documentName: reader.documentName,
        hasReader: !!reader
      });
      
      return { reader };
    }
    
    // Y.js format - use existing document resolution
    log.log('[createDocumentReader] ✅ Creating YjsReader for Y.js document:', {
      docId: documentId.substring(Math.max(0, documentId.length - 8)),
      documentName,
      format: 'yjs'
    });
    
    // Use existing Y.js document resolution (unchanged)
    const { doc, error } = await getYjsDocContext(documentId, context);
    
    if (error || !doc) {
      log.error('[createDocumentReader] Y.js document resolution failed:', {
        docId: documentId,
        error: error || 'Unknown error'
      });
      return {
        reader: null,
        error: error || 'Y.js document not accessible'
      };
    }
    
    // Lazy load to avoid circular dependencies
    if (!YjsDocumentReaderClass) {
      const module = await import('./readers/YjsDocumentReader');
      YjsDocumentReaderClass = (module as any).YjsDocumentReader;
    }
    
    const reader = new YjsDocumentReaderClass(
      documentId,
      documentName,
      doc.yDoc,
      doc.yxml,
      doc.metadata
    );
    
    return { reader };
    
  } catch (error) {
    log.error('[createDocumentReader] Failed to create reader:', {
      docId: documentId,
      error: error instanceof Error ? error.message : String(error)
    });
    return {
      reader: null,
      error: error instanceof Error ? error.message : 'Unknown error creating document reader'
    };
  }
}

